import {
  ArrowRight,
  ArrowUpRight,
  Check,
  Frame,
  Layers,
  PlugZap,
  SlidersHorizontal,
} from "lucide-react";
import { CanvasDemo } from "./canvas-demo";
import { Action, ClosingCTA, CodeBlock, PageIntro } from "./ui";
import { IndustryProjectGrid } from "./home-page";
import { industryProjects } from "@/lib/marketing-projects";

export { HomePage } from "./home-page";

const productFeatures = [
  {
    icon: Frame,
    label: "01 / Make space",
    title: "One idea. As many frames as it needs.",
    body: "Create desktop screens, mobile layouts, and interface variations side by side. Pan, zoom, and fit the canvas to see how the pieces relate.",
    items: [
      "Frames with nested elements",
      "Desktop and mobile sample designs",
      "Preview individual screens",
    ],
  },
  {
    icon: SlidersHorizontal,
    label: "02 / Get it right",
    title: "Big picture, meet small details.",
    body: "Pin feedback exactly where it belongs. People and agents can reply in the same thread, track what is new, and mark a comment handled when the work is done.",
    items: [
      "Pinned comments with colleague and agent replies",
      "New, In progress, and Handled statuses",
      "Search and filters for status, page, author, and mentions",
      "Viewer and commenter invitations",
    ],
  },
  {
    icon: PlugZap,
    label: "03 / Build together",
    title: "The same canvas, another perspective.",
    body: "Agents inspect and edit the live design through MCP or the REST API. Changes arrive in your browser, with an activity history that shows who changed what.",
    items: [
      "Named, revocable agent keys",
      "Atomic batches and revision checks",
      "Shared comment threads and statuses",
    ],
  },
  {
    icon: Layers,
    label: "04 / Keep going",
    title: "Your next step is an open one.",
    body: "Save your work locally, revisit it later, or export a portable design document. Take an SVG into your next visual workflow, or use JSON in your own tools.",
    items: [
      "Private accounts and durable storage",
      "SVG and JSON exports",
      "Native design chat and open agent connections",
    ],
  },
];

export function ProductPage() {
  return (
    <>
      <PageIntro
        eyebrow="The workspace"
        title={
          <>
            Space to explore.
            <br />
            Tools to be precise.
          </>
        }
      >
        A visual home for your next product idea. Work directly on the canvas,
        invite an agent into the process, and keep every element editable.
      </PageIntro>
      <div className="mk-container">
        <CanvasDemo />
      </div>
      <section className="mk-feature-list mk-container">
        {productFeatures.map(({ icon: Icon, label, title, body, items }) => (
          <article key={label}>
            <div className="mk-feature-symbol">
              <Icon size={52} strokeWidth={1} />
              <span>{label}</span>
            </div>
            <div>
              <h2>{title}</h2>
              <p>{body}</p>
              <ul className="mk-check-list">
                {items.map((item) => (
                  <li key={item}>
                    <Check size={15} />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </article>
        ))}
      </section>
      <section className="mk-note mk-container">
        <span className="mk-eyebrow">A clear starting point</span>
        <h2>Made for interface design.</h2>
        <p>
          This first version uses positioned elements and fixed text boxes. Auto
          layout, reusable component instances, interactive prototypes, and
          application code generation are not available yet.
        </p>
        <a href="/changelog" className="mk-text-link">
          See what’s in this release <ArrowUpRight size={15} />
        </a>
      </section>
      <ClosingCTA />
    </>
  );
}

export function UseCasesPage({ industrySlug = "" }: { industrySlug?: string }) {
  const selected = industryProjects.find(industry => industry.slug === industrySlug) || null;
  const projects = selected ? [selected.projectName] : industryProjects.map(industry => industry.projectName);
  return (
    <div className="mk-destination mk-home mk-home-container mk-use-cases-redesign">
      <header className="mk-use-cases-hero">
        <h1>Use cases by industry</h1>
      </header>
      <nav className="mk-industry-filters" aria-label="Industries">
        <a href="/use-cases" aria-current={!selected ? "page" : undefined}>All</a>
        {industryProjects.map(industry => <a
          key={industry.slug}
          href={`/use-cases/${industry.slug}`}
          aria-current={selected?.slug === industry.slug ? "page" : undefined}
        >{industry.label}</a>)}
      </nav>
      <section className="mk-industry-results" aria-labelledby="industry-results-title">
        <header>
          <h2 id="industry-results-title">{selected?.label || "All industries"}</h2>
          <p>{selected?.description || "AI-assisted design helps teams explore directions faster, turn references into editable concepts, and iterate with agents while keeping human creative direction in control."}</p>
        </header>
        <IndustryProjectGrid projectNames={projects} />
      </section>
    </div>
  );
}

export function PricingPage() {
  const plans = [
    {
      eyebrow: "Free credit on registration",
      title: "Start free",
      description: "Use your registration credit to explore the full workflow.",
      action: "Start creating",
      href: "/app?auth=signup",
      features: [
        "Create your first canvas",
        "Design screens with an agent",
        "Edit and iterate on designs",
        "Try sharing and collaboration",
      ],
    },
    {
      eyebrow: "Top up from €5",
      title: "Add credits anytime",
      description: "Buy more credits only when you need them. No recurring plan.",
      action: "Top up credits",
      href: "/app?workspace=account&account=billing",
      features: [
        "Design websites and landing pages",
        "Create product and app interfaces",
        "Explore multiple design directions",
        "Refine existing projects with agents",
      ],
    },
    {
      eyebrow: "Need something else?",
      title: "Contact us",
      description: "For larger usage, teams, custom workflows, or specific requirements.",
      action: "Contact us",
      href: "/support",
      features: [
        "Larger credit requirements",
        "Team and organization needs",
        "Custom integrations",
        "Tailored support",
      ],
    },
  ];
  return (
    <div className="mk-destination mk-home mk-home-container mk-pricing-redesign">
      <header className="mk-pricing-hero">
        <h1>Pay as you go. No monthly subscriptions.</h1>
        <p>You only pay for the work you do on Proxima Forma.</p>
      </header>
      <section className="mk-pricing-plans" aria-label="Proxima Forma pricing options">
        {plans.map((plan, index) => (
          <article key={plan.title}>
            <span className="mk-destination-label">{plan.eyebrow}</span>
            <h2>{plan.title}</h2>
            <p>{plan.description}</p>
            <ul>
              {plan.features.map((feature) => (
                <li key={feature}><Check size={16} aria-hidden="true" />{feature}</li>
              ))}
            </ul>
            <Action href={plan.href} secondary={index !== 1}>{plan.action}</Action>
          </article>
        ))}
      </section>
      <p className="mk-pricing-note">
        No subscription. No commitment. Add credits when you need them and use
        them at your own pace.
      </p>
    </div>
  );
}

export function ChangelogPage() {
  return (
    <>
      <PageIntro
        eyebrow="What’s new"
        title={
          <>
            A work in progress.
            <br />
            By design.
          </>
        }
      >
        A record of what’s available, as the workspace takes shape.
      </PageIntro>
      <section className="mk-release-log mk-container">
        <aside>
          <span className="mk-price-badge">v0.2.0</span>
          <p>Accounts and shared review</p>
        </aside>
        <article>
          <h2>A canvas for your whole conversation.</h2>
          <p>
            Register for private canvases, connect agents with scoped access,
            and invite reviewers. A simpler canvas interface puts pages and
            comments in floating panels.
          </p>
          <ul>
            <li>Pinned comments, replies, statuses, search, and filters.</li>
            <li>Agent access to comment threads through MCP and the API.</li>
            <li>
              View-only and commenting invitations, key expiry, revocation, and
              passwordless email sign-in.
            </li>
          </ul>
          <p>
            Local preview requires an email sender for account sign-in. Public
            hosting, paid plans, and public ChatGPT directory availability depend on deployment setup.
          </p>
        </article>
      </section>
      <section className="mk-release-log mk-container">
        <aside>
          <span className="mk-price-badge">v0.1.0</span>
          <p>Initial local release</p>
        </aside>
        <article>
          <h2>The first shared canvas.</h2>
          <p>
            Proximaforma brings visual editing, a REST API, and an MCP server
            into one local design workspace.
          </p>
          {[
            [
              "The canvas",
              "Frames, text, shapes, buttons, and images. Nested layers, a property inspector, drag and resize, preview, undo and redo, and SVG or JSON export.",
            ],
            [
              "Your agents, connected",
              "MCP over stdio and Streamable HTTP, thirteen design tools, named agent keys, live changes, and an activity panel.",
            ],
            [
              "Work that stays with you",
              "SQLite persistence, atomic editing batches, and revision checks that reject stale changes.",
            ],
            [
              "A clearer view",
              "The canvas repaints when zooming to keep text and shapes sharp instead of enlarging a cached image.",
            ],
            [
              "Vector drawing",
              "Pen and line tools, polygon and star presets, editable anchors and Bézier handles, stroke styles, and three dedicated MCP vector tools.",
            ],
            [
              "A home around the workspace",
              "Product and use-case pages, integration guides, pricing information, searchable documentation, and a data overview.",
            ],
          ].map(([title, text]) => (
            <div key={title}>
              <h3>{title}</h3>
              <p>{text}</p>
            </div>
          ))}
          <a href="/docs" className="mk-text-link">
            Explore the documentation <ArrowUpRight size={16} />
          </a>
        </article>
      </section>
      <ClosingCTA />
    </>
  );
}

export function PrivacyPage() {
  return (
    <>
      <PageIntro eyebrow="Privacy" title={<>Your workspace.<br />A clear view of your data.</>}>
        How ProximaForma handles accounts, designs, and conversations.
      </PageIntro>
      <article className="mk-prose mk-container mk-data-page">
        <p>Updated 18 September 2026. ProximaForma at proximaforma.com is operated by RatioSky LTD. For privacy requests, contact <a href="mailto:support@proximaforma.com">support@proximaforma.com</a>.</p>
        <h2>Information we store</h2>
        <p>We store your account name and email, short-lived sign-in-code hashes, project memberships, designs, uploaded references, comments, activity, agent conversations, generated images, and usage and billing records. Accounts created under an earlier release may retain unused legacy password and recovery-code hashes until the account signs in by email or is deleted. We use this information to provide your workspace, control access, meter paid features, and investigate service problems.</p>
        <h2>Hosting and service providers</h2>
        <p>The hosted service uses Amazon Web Services in Frankfurt, Germany for its application database and object storage. Content is delivered through a global content delivery network. AWS also processes operational logs, backups, and verification emails. Support messages are handled through Microsoft 365. Payments are processed by Stripe; we retain payment references and credit records, while payment-card details are handled by Stripe.</p>
        <h2>AI conversations and project images</h2>
        <p>When you use Forma, your messages and the canvas information its tools read are sent to OpenAI for text or voice generation. A voice conversation sends microphone audio while the call is active. We save conversation text, but do not save audio recordings. Uploaded images and images retained from earlier releases are stored with your workspace. OpenAI processes conversation requests under its applicable service terms and data policies.</p>
        <h2>Connected apps and agents</h2>
        <p>A connected agent can read or change only the canvases and actions allowed by your connection and account membership. Information it reads is shared with the service operating that agent. When you consent to an email identity scope, we share your verified email address with the connected app, including for enterprise email-domain restrictions. Verification requires a code sent to your email; an account profile alone does not establish ownership.</p>
        <p>You can inspect and revoke agent access in Connect agent. OAuth access tokens expire after one hour; new connections renew automatically until you revoke access. Revocation prevents further access, but does not erase information already received by another service.</p>
        <h2 id="browser-storage">Browser storage and credentials</h2>
        <p>An HTTP-only session cookie keeps you signed in for up to seven days. Browser storage remembers workspace preferences. This build does not include advertising or third-party marketing tracking scripts. Sign-in codes, session tokens, invitation tokens, and API keys are stored as hashes. Sign-in codes expire after ten minutes, work once, and permit at most five attempts. Design exports do not include agent credentials.</p>
        <p>We use browser storage to remember when you dismiss the cookie notice. The notice describes our essential sign-in cookie; dismissing it does not enable analytics or advertising cookies.</p>
        <h2>External resources</h2>
        <p>Linked images and fonts may be requested from their original providers. Those providers receive normal request information, such as your IP address. An exported SVG can retain external image links.</p>
        <h2>Retention and deletion</h2>
        <p>Workspace data is retained while you use the service, unless you remove it or request deletion. Deleting a canvas removes it from the workspace and schedules cleanup of its related database records. Immutable files and older object versions can remain in storage until separate cleanup; contact us for a complete deletion request. Operational logs and scheduled database backups have a fourteen-day retention period. Billing and transaction records are retained separately where needed for accounting and resolving payment issues.</p>
        <h2>Your choices and requests</h2>
        <p>You can export designs as JSON or SVG, delete canvases, and revoke connected agents. Contact <a href="mailto:support@proximaforma.com">support@proximaforma.com</a> to request access, correction, or deletion of account data, or to ask about retention. We may need to verify your identity before acting on an account request.</p>
        <h2>Self-hosted installations</h2>
        <p>If you run the software yourself, its operator controls storage, backups, and connected providers. The local default uses a SQLite database in the installation’s data directory. This page describes the service at proximaforma.com.</p>
        <a href="/support" className="mk-text-link">Contact support <ArrowUpRight size={15} /></a>
      </article>
    </>
  );
}

export function SupportPage() {
  return (
    <>
      <PageIntro eyebrow="Support" title={<>Help with<br />your workspace.</>}>
        Account, connection, billing, and privacy support for ProximaForma.
      </PageIntro>
      <article className="mk-prose mk-container mk-data-page">
        <h2>Contact us</h2>
        <p>Email <a href="mailto:support@proximaforma.com">support@proximaforma.com</a>. Include what you were trying to do, the approximate time, and the error you saw. Share only the design content needed to explain the issue. Never send sign-in codes, verification codes, or agent keys.</p>
        <h2>Connecting ChatGPT</h2>
        <p>Start with the <a href="/connect/chatgpt">ChatGPT connection guide</a>. Sign in to ProximaForma, review the requested access, select Give access, and return to the connection window. If asked to verify your email, enter the code sent to your account address. Connections can be revoked from Connect agent in the workspace.</p>
        <h2>Accounts and billing</h2>
        <p>For an account or payment issue, include your account email and, if relevant, the receipt reference. Do not include payment-card details. We may ask you to verify ownership before changing account information.</p>
        <h2>Privacy requests</h2>
        <p>Use the same support address for data access, correction, and deletion requests. Our <a href="/privacy">privacy page</a> explains the information we store and the services that process it.</p>
        <a href="/docs" className="mk-text-link">Browse documentation <ArrowUpRight size={15} /></a>
      </article>
    </>
  );
}

export function NotFoundPage() {
  return (
    <>
      <PageIntro
        eyebrow="404 / A little off-canvas"
        title={
          <>
            This page hasn’t
            <br />
            taken shape.
          </>
        }
      >
        The address may have changed, or the page may not exist. Your workspace
        is one click away.
      </PageIntro>
      <div className="mk-container mk-not-found-actions">
        <Action />
        <Action href="/" secondary>
          Back to the homepage
        </Action>
        <a className="mk-text-link" href="/docs">
          Browse the docs <ArrowUpRight size={15} />
        </a>
      </div>
    </>
  );
}
